/**
 * Triggers the browser's save dialog for a remote file URL instead of
 * navigating to it / opening a new tab.
 *
 * File URLs are presigned (cross-origin) S3 URLs, so an anchor's `download`
 * attribute alone is ignored by the browser. We fetch the file as a blob and
 * download via an object URL so the filename + save dialog are honored. If the
 * fetch is blocked (CORS / network), we fall back to a direct anchor download.
 */
export async function downloadFileFromUrl(url: string, filename: string): Promise<void> {
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    triggerAnchorDownload(objectUrl, filename);
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
  } catch {
    // Cross-origin fetch blocked or network error — fall back to a direct
    // anchor download (saves for same-origin, opens otherwise).
    triggerAnchorDownload(url, filename);
  }
}

function triggerAnchorDownload(href: string, filename: string): void {
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}
